import React, { useEffect, useState } from 'react'

import { useToast } from '@zet/common/components/toast'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { webStates } from '@zet/customer/src/utils/addCustomerStates'
import { Stack } from '@zet/design-system'
import ScreenLoader from '@zet/feature-brands/screens/brandListV2/screenLoader'
import { i18n } from '@zet/localization'
import { replaceLoginHash } from '@zet/utils/loginHash'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'
import _ from 'lodash'

import { useGetDecryptToken } from '../../hooks/useGetDescrptJwt'
import useJPBBridge from '../../hooks/useJPBBridge'
import { useJwtToken } from '../../hooks/useJwtToken'
import useNativeAppBridge from '../../hooks/useNativeAppBridge'
import { sendAnalyticsData } from '../../utils/analyticsEvents'

export const CustomerLoading = () => {
  const [token, setToken] = useState<string>('')
  const { getJwtToken } = useJwtToken()
  const { isIOS } = useJPBBridge()
  const { changeFooterVisibility, changeHeaderVisibility } =
    useNativeAppBridge()
  const { setCustomerPhoneNumber, setVariantId } = useAddCustomerStore()
  const { isLoading: isThemeLoading } = useGetAgentDetailsAndTheme()
  const navigation = useNavigation()
  const toast = useToast()

  const showErrorToast = (message?: string) => {
    toast?.show({
      intent: 'error',
      heading: i18n.t('toastMessages.error'),
      description: message || i18n.t('toastMessages.somethingWentWrong'),
      hideAfter: 3000,
    })
  }

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await getJwtToken()
        await changeFooterVisibility(0)
        await changeHeaderVisibility(0)
        let parsedResponse = response
        if (!isIOS()) {
          parsedResponse = JSON.parse(response)
        }
        const jwt = isIOS() ? parsedResponse : parsedResponse?.data?.jwtToken
        if (!jwt) throw new Error('JWT not found in response')
        setToken(jwt)
      } catch (err) {
        console.error('Failed to get JWT token:', err)
        showErrorToast()
      }
    }

    fetchToken()
  }, [getJwtToken])

  const {
    data: decodedToken,
    isLoading: isTokenDecrypting,
    error: decryptError,
  } = useGetDecryptToken(token)

  useEffect(() => {
    if (decryptError) {
      console.error('Token decryption failed:', decryptError)
      showErrorToast()
    }
  }, [decryptError])

  useEffect(() => {
    if (!decodedToken) return

    const urlParams = new URLSearchParams(window.location.search)
    const variantId = encodeURIComponent(urlParams.get('productId') || '')

    const { action, actionData } = decodedToken
    const { phoneNumber, customerId, jfsConsent, hash = '' } = actionData || {}
    if (hash) {
      replaceLoginHash(hash)
    }

    if (_.isObject(jfsConsent)) {
      sessionStorage.setItem('jfsConsent', JSON.stringify(jfsConsent))
    }

    if (phoneNumber) {
      setCustomerPhoneNumber(phoneNumber)
    }

    if (action && webStates[action]) {
      const navigationParams: Record<string, any> = { phoneNumber }

      if (variantId) {
        navigationParams.variantId = variantId
        navigationParams.categoryId = 15
        setVariantId(Number(variantId))
      }

      if (customerId !== undefined) {
        sessionStorage.setItem('customerId', String(customerId))
        navigationParams.customerId = customerId
      } else {
        sessionStorage.removeItem('categoryId')
      }
      sendAnalyticsData({ screen: action, type: 'screen' })
      navigation.replace(webStates[action], navigationParams)
    } else {
      console.warn('Unknown action or missing navigation path', action)
    }
  }, [decodedToken])

  if (isThemeLoading || isTokenDecrypting || !decodedToken) {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <ScreenLoader showLottie isJFSFlow />
      </Stack>
    )
  }

  return <Stack className="flex-1 bg-white" />
}
